import { useEffect } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { DrawerProvider } from '@/features/drawer/DrawerProvider';
import ServiceModal from '@/features/serviceModal/ServiceModal';
import { Snacks } from '@/features/snacks/Snacks';
import { selectSystemIsLoading } from '@/features/system/store/selectors';
import { getTenantConfiguration } from '@/features/system/store/systemSlice';
import { runCycleTokenChecker } from '@/domains/auth/store/authSlice';
import { selectUser } from '@/domains/user/store/selectors';
import { getUserData } from '@/domains/user/store/userSlice';
import { Container } from '@/App.styles';
import { GlobalLoader } from '@/components/feedback/GlobalLoader/GlobalLoader';
import AppHelmet from '@/components/helpers/AppHelmet/AppHelmet';
import ErrorBoundary from '@/components/helpers/ErrorBoundary/ErrorBoundary';
import { useNavigationTracker } from '@/hooks/useNavigationTracker';
import { useOverrideLog } from '@/hooks/useOverrideLog';
import { usePagePersistedLoad } from '@/hooks/usePagePersistedLoad';
import { usePreloadPages } from '@/hooks/usePreloadPages';
import { useServerSideEvents } from '@/hooks/useServerSideEvents';
import { useSystemCheckPageActivity } from '@/hooks/useSystemCheckPageActivity';
import { useSystemCheckUserActivity } from '@/hooks/useSystemCheckUserActivity';
import NavigationService from '@/services/NavigationService/NavigationService';
import TokenService from '@/services/TokenService/TokenService';
import { useDispatch, useSelector } from '@/store/hooks';
import { clearAuthData } from './domains/user/utils/clearAuthData';
import useCloseAllModalsOnBack from './hooks/useCloseAllModalsOnBack';

export const App = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const token = TokenService.getTokenData();

  const systemIsLoading = useSelector(selectSystemIsLoading);
  const user = useSelector(selectUser);

  useServerSideEvents(import.meta.env.VITE_SSE_USER_HUB_URL);
  useSystemCheckPageActivity();
  useSystemCheckUserActivity();
  useCloseAllModalsOnBack();
  usePagePersistedLoad();
  useOverrideLog();
  useNavigationTracker();
  usePreloadPages();

  useEffect(() => {
    if (token) {
      dispatch(runCycleTokenChecker());
    } else {
      clearAuthData();
    }
  }, [token]);

  useEffect(() => {
    !user && token && dispatch(getUserData());
  }, [user, token]);

  useEffect(() => {
    NavigationService.setNavigate(navigate);
  }, [navigate]);

  useEffect(() => {
    dispatch(getTenantConfiguration());
  }, []);

  return (
    <Container>
      <AppHelmet />
      <GlobalLoader status={systemIsLoading} />
      <DrawerProvider />
      <Snacks />
      <ServiceModal />
      <ErrorBoundary isGlobal>
        <Outlet />
      </ErrorBoundary>
    </Container>
  );
};
